import {
  Link,
  TileRoot,
  TileFloat,
  Badge,
  TileContent,
  TileBody,
  TileHeading,
  TileDescription,
  TileFooter,
  TileMedia,
  LinkInvBlock,
} from '@global-ecom/nitro-uds/elements';

import { useTranslate } from 'hooks/useTranslations';
import { buildProductUrl } from 'utils/buildProductUrl';
import { SanityImage as SanityImageComponent } from 'ui/components/SanityImage';
import { useGA4Events } from 'hooks/useGA4Events';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { setGa4ItemInMap } from 'utils/ga4Items';

import { LaunchCalendarItem } from './LaunchCalendarList';

type LaunchCalendarTileProps = {
  item: LaunchCalendarItem;
  position?: number;
  itemListId?: string;
  itemListName?: string;
} & React.ComponentProps<'li'>;

export const LaunchCalendarTile = (props: LaunchCalendarTileProps) => {
  const { item, itemListId, itemListName, position } = props;
  const { setProduct, fireClickProductImage } = useGA4Events();
  const {
    regionCode,
    staticFeatures: {
      searchLocalImagesOnDropCalendar,
      showHeaderSubHeaderProductDetail,
    },
  } = useSiteConfig();
  const t = useTranslate();
  const date = new Date(item.launchDate);
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  const getImageURL = () => {
    const baseURL = `https://images.puma.com/image/upload/f_auto,q_auto,b_rgb:fafafa,w_600,h_600/global/${item.masterId}/${item.color}`;
    const imageViewPath = item?.imageView ? `/${item.imageView}` : '';
    const regionPath = searchLocalImagesOnDropCalendar
      ? `/fnd/${regionCode}`
      : '';

    return `${baseURL}${imageViewPath}${regionPath}/fmt/png`;
  };

  const productUrl =
    !item.disableProductLink && item?.masterId
      ? buildProductUrl(item.masterId, item.productName, {
          color: item.color,
        })
      : item?.cta?.[0]?.link;

  return (
    <TileRoot fill="content" layout="stack" asChild>
      <li
        onClick={() => {
          fireClickProductImage(getImageURL());
          setGa4ItemInMap(`${item.masterId}_${item.color}`, {
            lid: itemListId,
            lname: itemListName,
            idx: position,
          });
          setProduct(item);
        }}
      >
        {item.appExclusive && (
          <TileFloat justify="start">
            <Badge label={t('appExclusive')} />
          </TileFloat>
        )}
        <TileContent justify="center" align="space-between">
          <TileBody>
            <TileHeading
              balance
              size={{ mobile: 'sm', tablet: 'base', desktop: 'sm' }}
            >
              {showHeaderSubHeaderProductDetail
                ? item.header
                : item.launchCalendarProductName}
            </TileHeading>
            {showHeaderSubHeaderProductDetail && item.subHeader && (
              <TileDescription
                size="sm"
                className={'text-puma-gray-60 line-clamp-2'}
              >
                {item.subHeader}
              </TileDescription>
            )}
            <TileDescription size="sm">
              {item?.launchCopy
                ? item.launchCopy
                : `${t('available').toUpperCase()} ${t('dateFormat', {
                    day,
                    month,
                    year,
                  })}`}
            </TileDescription>
          </TileBody>
          <TileFooter>
            {productUrl ? (
              <Link
                label={item?.ctaCopy ? item.ctaCopy : t('learnMore')}
                target={item.appExclusive ? '_blank' : '_self'}
                variant="secondary"
                href={productUrl}
              />
            ) : null}
          </TileFooter>
        </TileContent>
        <TileMedia>
          {item.productImage ? (
            <SanityImageComponent source={item.productImage} />
          ) : (
            <picture>
              <img src={getImageURL()} alt="" />
            </picture>
          )}
        </TileMedia>
        {/*focusable=false since we already render the same visual link in the tile footer */}
        {productUrl ? (
          <LinkInvBlock
            target={item.appExclusive ? '_blank' : '_self'}
            href={productUrl}
            focusable={false}
          />
        ) : null}
      </li>
    </TileRoot>
  );
};
